import axios from "axios";

export const BACKEND_URL = (
  process.env.REACT_APP_BACKEND_URL || "http://localhost:8000"
).replace(/\/$/, "");

const API = `${BACKEND_URL}/api`;

// Send cookies with Axios requests automatically
axios.defaults.withCredentials = true;

const QQ_PORTAL = process.env.REACT_APP_QQ_PORTAL_URL || "http://localhost:7001/my-tools";

// Request interceptor to automatically attach the QuirkyQode token
axios.interceptors.request.use(
  config => {
    const token = localStorage.getItem('qq_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  error => Promise.reject(error)
);

// Response interceptor to handle authentication & software lock checks
axios.interceptors.response.use(
  res => res,
  err => {
    // 401: Unauthorized -> Redirect back to QuirkyQode SSO hub
    if (err.response?.status === 401) {
      localStorage.removeItem('qq_token');
      window.location.href = QQ_PORTAL;
    }
    // 402: Payment Required (Software Lock) -> Reload to trigger lock overlay
    if (err.response?.status === 402) {
      window.location.reload();
    }
    return Promise.reject(err);
  }
);


export const api = {
  // Auth
  login: (email, password) =>
    axios.post(`${API}/auth/login`, { email, password }),
  
  logout: () =>
    axios.post(`${API}/auth/logout`),
  
  getMe: () =>
    axios.get(`${API}/auth/me`),

  // Influencers CRUD & Stats
  getInfluencers: (params = {}) => 
    axios.get(`${API}/influencers`, { params }),

  getInfluencerStats: () => 
    axios.get(`${API}/influencers/stats`),

  getInfluencer: (id) => 
    axios.get(`${API}/influencers/${id}`),

  createInfluencer: (data) => 
    axios.post(`${API}/influencers`, data),

  updateInfluencer: (id, data) => 
    axios.put(`${API}/influencers/${id}`, data),

  deleteInfluencer: (id) => 
    axios.delete(`${API}/influencers/${id}`),

  seedInfluencers: () => 
    axios.post(`${API}/influencers/import/seed`),

  cleanupDummyInfluencerData: () =>
    axios.delete(`${API}/influencers/cleanup/dummy-data`),

  importInfluencers: (file) => {
    const formData = new FormData();
    formData.append("file", file);
    return axios.post(`${API}/influencers/import/excel`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  },

  getInfluencerMetricsHistory: (influencerId, platform = null, days = 30) =>
    axios.get(`${API}/influencers/${influencerId}/snapshots`, {
      params: { platform, days },
    }),

  // Campaign management & stats
  getInfluencerCampaigns: (params = {}) =>
    axios.get(`${API}/influencer-campaigns`, { params }),

  getCampaignStats: () => 
    axios.get(`${API}/influencer-campaigns/stats`),

  getInfluencerCampaign: (id) => 
    axios.get(`${API}/influencer-campaigns/${id}`),

  createInfluencerCampaign: (data) =>
    axios.post(`${API}/influencer-campaigns`, data),

  updateInfluencerCampaign: (id, data) =>
    axios.put(`${API}/influencer-campaigns/${id}`, data),

  deleteInfluencerCampaign: (id) =>
    axios.delete(`${API}/influencer-campaigns/${id}`),

  // Campaign Influencer associations
  getCampaignInfluencers: (campaignId) =>
    axios.get(`${API}/influencer-campaigns/${campaignId}/influencers`),

  addInfluencerToCampaign: (campaignId, data) =>
    axios.post(`${API}/influencer-campaigns/${campaignId}/influencers`, data),

  bulkAddInfluencersToCampaign: (campaignId, influencerIds) =>
    axios.post(`${API}/influencer-campaigns/${campaignId}/influencers/bulk`, {
      influencer_ids: influencerIds,
    }),

  updateCampaignInfluencer: (campaignId, influencerId, data) =>
    axios.put(
      `${API}/influencer-campaigns/${campaignId}/influencers/${influencerId}`,
      data
    ),

  removeInfluencerFromCampaign: (campaignId, influencerId) =>
    axios.delete(
      `${API}/influencer-campaigns/${campaignId}/influencers/${influencerId}`
    ),

  syncCampaignPerformance: (campaignId) =>
    axios.post(`${API}/influencer-campaigns/${campaignId}/sync-performance`),

  getCampaignSyncStatus: (campaignId, jobId) =>
    axios.get(`${API}/influencer-campaigns/${campaignId}/sync-status/${jobId}`),

  getApiCredits: () =>
    axios.get(`${API}/influencer-campaigns/api-credits`),

  getCampaignPostsByHandle: () =>
    axios.get(`${API}/influencer-campaigns/all-posts-by-handle`),

  // RapidAPI Config
  getMetaAdsStatus: () => axios.get(`${API}/influencers/config/rapidapi`),
  
  // Instagram Tracker persist endpoints
  getSavedTrackers: () => 
    axios.get(`${API}/instagram-tracker`),
    
  saveTracker: (username, data) => 
    axios.post(`${API}/instagram-tracker/${username}`, data),
    
  deleteTracker: (username) => 
    axios.delete(`${API}/instagram-tracker/${username}`),

  // Auth User Management & Password Reset
  registerUser: (data) =>
    axios.post(`${API}/auth/register`, data),
    
  getUsers: () =>
    axios.get(`${API}/auth/users`),

  resetPassword: (currentPassword, newPassword) =>
    axios.post(`${API}/auth/reset-password`, { currentPassword, newPassword }),

  updateUser: (email, data) =>
    axios.put(`${API}/auth/users/${email}`, data),

  deleteUser: (email) =>
    axios.delete(`${API}/auth/users/${email}`),

  resetUserPassword: (email, newPassword) =>
    axios.post(`${API}/auth/users/${email}/reset-password`, { newPassword }),

  fetchYouTubeChannel: (handle) =>
    axios.get(`${API}/influencers/youtube/channel/${encodeURIComponent(handle)}`),

  fetchYouTubeVideo: (url) =>
    axios.get(`${API}/influencers/youtube/video`, { params: { url } }),

  getYouTubeRecentVideos: (influencerId) =>
    axios.get(`${API}/influencers/${influencerId}/youtube/videos`),

  updateYouTubeTrackedVideos: (influencerId, videos) =>
    axios.put(`${API}/influencers/${influencerId}/youtube/videos`, videos),

  syncYouTubeMetrics: (influencerId) =>
    axios.post(`${API}/influencers/${influencerId}/youtube/sync`),
};

export default api;
